import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createAccountParams, TokenEntity } from '@interpals/api-web';

import api from 'api';
import { OAuthProviderName } from 'types';
import { initLoggedInUser } from 'redux/auth/actions';
import { ReduxState } from 'redux/root-reducer';
import { makeOauthRedirectUri } from 'routes';

const getRedirectUri = (provider: OAuthProviderName) => {
    return provider === 'apple'
        ? `https://www.interpals.net/app/oauth-redirect/apple/`
        : `https://${window.location.host}` + makeOauthRedirectUri(provider);
};

export const createOAuthUrl = createAsyncThunk('create/oauth/url', async (source: OAuthProviderName) => {
    await api
        .makeOAuthUrl({ id: source, redirect_uri: getRedirectUri(source) })
        .then((data) => data)
        .then((url) => window.location.replace(url));
});

interface CreateOAuthParams {
    url: string;
    provider: OAuthProviderName;
}

// This is user data that was fetched from the OAuth provider by the backend
// but is returned to the client because some required fields are missing. The client
// should then pass this data along with the missing items back to createAccount.
// Todo: Add swagger annotation on backend
export type OAuthCreateAccountParams = Partial<createAccountParams> & {
    email_verified?: boolean;
    email?: string;
    sex?: string;
    birthday?: string;
    latitude?: string;
    longitude?: string;
    timezone?: string;
    city_id?: number;
    city_name_en?: string;
    country_code?: string;
    region_id?: number;
    home_city_name_en?: string;
    home_city_id?: number;
    home_region_id?: number;
    home_country_code?: string;
    oauth_provider?: OAuthProviderName;
    oauth_access_token?: string;
    oauth_id?: string;
};

export type OAuthErrorCodeEnum = 'oauth_missing_data_to_create_account' | 'account_suspended' | 'social_account_linked';

export interface OAuthError {
    response: Response;
    error: {
        code: OAuthErrorCodeEnum;
        message: string;
        extra?: {
            oauthData?: OAuthCreateAccountParams;
        };
    };
}

export const createOAuth = createAsyncThunk<
    TokenEntity,
    CreateOAuthParams,
    { rejectValue: OAuthError; state: ReduxState }
>('create/oauth', async (params: CreateOAuthParams, { getState, dispatch, rejectWithValue }) => {
    const { auth } = getState();

    const urlCode = () => {
        const code = params.url.match(/code=([^&]+)/);

        if (params.provider === 'google') {
            return code ? decodeURIComponent(code[1]) : '';
        } else {
            return code ? code[1] : '';
        }
    };

    return await api
        .createOAuth({
            provider: params.provider,
            code: urlCode(),
            redirect_uri: getRedirectUri(params.provider),
        })
        .then((token) => {
            if (!auth.authorized) {
                dispatch(initLoggedInUser(token));
            }

            return token;
        })
        .catch(async (response) => {
            return rejectWithValue({
                response,
                error: (await response.clone().json())?.error,
            });
        });
});

export const deleteOAuth = createAsyncThunk('delete/oauth', async (id: OAuthProviderName) => {
    await api.deleteOAuth({ id });

    return id;
});

export const clearOauthData = createAction('clear/oauth/data');
